import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router";
import { toast } from "sonner";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Skeleton,
  StatusDot,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@dg/ui";
import { useAuth } from "../contexts/auth.js";
import {
  getAdminUsers,
  updateAdminUser,
  deleteAdminUser,
  getAdminInvites,
  sendInvite,
  resendInvite,
  deleteInvite,
  getAdminAuditLogs,
  isApiError,
  type AdminUser,
  type AdminInvite,
  type AuditLogEntry,
} from "../lib/api.js";

// ── Helpers ────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function statusBadge(status: AdminUser["status"]) {
  const variants: Record<
    AdminUser["status"],
    "default" | "secondary" | "destructive" | "outline"
  > = {
    active: "default",
    pending: "secondary",
    suspended: "destructive",
    rejected: "outline",
    deleted: "outline",
  };
  return <Badge variant={variants[status]}>{status}</Badge>;
}

// ── Users tab ──────────────────────────────────────────────────────────────

function UsersTab() {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const load = useCallback(
    async (p = page) => {
      setLoading(true);
      try {
        const res = await getAdminUsers({
          page: p,
          ...(search ? { search } : {}),
          ...(filterStatus ? { status: filterStatus } : {}),
        });
        setUsers(res.data);
        setTotal(res.total);
      } catch {
        toast.error("Failed to load users.");
      } finally {
        setLoading(false);
      }
    },
    [page, search, filterStatus],
  );

  useEffect(() => {
    void load();
  }, [load]);

  async function handleAction(
    userId: string,
    action: "approve" | "reject" | "suspend" | "unsuspend" | "delete",
  ) {
    setActionLoading(userId);
    try {
      if (action === "delete") {
        await deleteAdminUser(userId);
        toast.success("User deleted.");
      } else {
        const statusMap = {
          approve: "active",
          reject: "rejected",
          suspend: "suspended",
          unsuspend: "active",
        } as const;
        await updateAdminUser(userId, { status: statusMap[action] });
        toast.success(`User ${action}d.`);
      }
      await load();
    } catch (err) {
      toast.error(isApiError(err) ? "Action failed." : "Something went wrong.");
    } finally {
      setActionLoading(null);
    }
  }

  const pending = users?.filter((u) => u.status === "pending") ?? [];
  const rest = users?.filter((u) => u.status !== "pending") ?? [];
  const totalPages = Math.ceil(total / 50);

  return (
    <div className="space-y-6">
      {/* Search + filter */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Input
          placeholder="Search name or email…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="sm:max-w-xs"
        />
        <Select
          value={filterStatus || "all"}
          onValueChange={(v) => {
            setFilterStatus(v === "all" ? "" : v);
            setPage(1);
          }}
        >
          <SelectTrigger className="sm:w-40">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Pending queue */}
      {pending.length > 0 && !filterStatus && (
        <div className="space-y-3">
          <h2 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
            Pending approval ({pending.length})
          </h2>
          {pending.map((u) => (
            <UserRow
              key={u.id}
              user={u}
              loading={actionLoading === u.id}
              onAction={handleAction}
            />
          ))}
          <Separator />
        </div>
      )}

      {/* All users */}
      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-16 rounded-lg" />
          ))}
        </div>
      ) : rest.length === 0 && pending.length === 0 ? (
        <p className="py-8 text-center text-muted-foreground">
          No users found.
        </p>
      ) : (
        <div className="space-y-2">
          {rest.map((u) => (
            <UserRow
              key={u.id}
              user={u}
              loading={actionLoading === u.id}
              onAction={handleAction}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}

function UserRow({
  user,
  loading,
  onAction,
}: {
  user: AdminUser;
  loading: boolean;
  onAction: (
    id: string,
    action: "approve" | "reject" | "suspend" | "unsuspend" | "delete",
  ) => void;
}) {
  const [showDelete, setShowDelete] = useState(false);

  return (
    <>
      {/* Desktop row / Mobile card */}
      <Card className="p-3">
        <div className="flex flex-wrap items-start gap-3">
          <div className="relative">
            <Avatar className="h-10 w-10">
              <AvatarImage src={user.avatarUrl ?? undefined} alt="" />
              <AvatarFallback>
                {(user.displayName ?? user.email ?? "?")[0]?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <span className="absolute -bottom-0.5 -right-0.5">
              <StatusDot
                variant={user.hasActiveSession ? "active" : "inactive"}
              />
            </span>
          </div>

          <div className="flex-1 min-w-0">
            <div className="font-medium truncate">
              {user.displayName ?? "—"}
            </div>
            <div className="text-xs text-muted-foreground truncate">
              {user.email ?? "—"}
            </div>
            <div className="flex flex-wrap gap-1 mt-1">
              {statusBadge(user.status)}
              <Badge variant="outline" className="text-xs">
                {user.role}
              </Badge>
              {user.providers.map((p) => (
                <Badge key={p} variant="outline" className="text-xs capitalize">
                  {p}
                </Badge>
              ))}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Joined {formatDate(user.createdAt)}
              {user.lastLoginAt && (
                <> · Last login {formatDate(user.lastLoginAt)}</>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-1.5 shrink-0">
            {user.status === "pending" && (
              <>
                <Button
                  size="sm"
                  disabled={loading}
                  onClick={() => onAction(user.id, "approve")}
                >
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  disabled={loading}
                  onClick={() => onAction(user.id, "reject")}
                >
                  Reject
                </Button>
              </>
            )}
            {user.status === "active" && (
              <Button
                size="sm"
                variant="outline"
                disabled={loading}
                onClick={() => onAction(user.id, "suspend")}
              >
                Suspend
              </Button>
            )}
            {user.status === "suspended" && (
              <Button
                size="sm"
                variant="outline"
                disabled={loading}
                onClick={() => onAction(user.id, "unsuspend")}
              >
                Unsuspend
              </Button>
            )}
            {user.status !== "deleted" && (
              <Button
                size="sm"
                variant="destructive"
                disabled={loading}
                onClick={() => setShowDelete(true)}
              >
                Delete
              </Button>
            )}
          </div>
        </div>
      </Card>

      <Dialog open={showDelete} onOpenChange={setShowDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete user</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This will anonymise{" "}
            <strong>{user.displayName ?? user.email ?? "this user"}</strong>'s
            account and revoke all sessions. The audit trail is preserved. This
            cannot be undone.
          </p>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setShowDelete(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={loading}
              onClick={() => {
                setShowDelete(false);
                onAction(user.id, "delete");
              }}
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── Invitations tab ────────────────────────────────────────────────────────

function InviteRow({
  inv,
  loading,
  onResend,
  onDelete,
}: {
  inv: AdminInvite;
  loading: boolean;
  onResend: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  const [showDelete, setShowDelete] = useState(false);

  return (
    <Card className="p-3">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="min-w-0">
          <div className="font-medium truncate">{inv.email}</div>
          <div className="text-xs text-muted-foreground">
            Sent {formatDate(inv.createdAt)} · Expires{" "}
            {formatDate(inv.expiresAt)}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Badge
            variant={
              inv.status === "accepted"
                ? "default"
                : inv.status === "expired"
                  ? "outline"
                  : "secondary"
            }
          >
            {inv.status}
          </Badge>
          {inv.status === "pending" && (
            <>
              <Button
                size="sm"
                variant="outline"
                disabled={loading}
                onClick={() => onResend(inv.id)}
              >
                Resend
              </Button>
              <Dialog open={showDelete} onOpenChange={setShowDelete}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="destructive" disabled={loading}>
                    Delete
                  </Button>
                </DialogTrigger>
                <DialogContent onInteractOutside={(e) => e.preventDefault()}>
                  <DialogHeader>
                    <DialogTitle>Delete invitation</DialogTitle>
                    <DialogDescription>
                      This will permanently revoke the invite link.
                    </DialogDescription>
                  </DialogHeader>
                  <p className="text-sm text-muted-foreground">
                    Delete invitation sent to <strong>{inv.email}</strong>? The
                    invite link will immediately stop working.
                  </p>
                  <div className="flex justify-end gap-2 mt-2">
                    <Button
                      variant="outline"
                      onClick={() => setShowDelete(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      disabled={loading}
                      onClick={() => {
                        setShowDelete(false);
                        onDelete(inv.id);
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </div>
    </Card>
  );
}

function InvitationsTab() {
  const [invites, setInvites] = useState<AdminInvite[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const load = useCallback(
    async (p = page) => {
      try {
        const res = await getAdminInvites(p);
        setInvites(res.data);
        setTotal(res.total);
      } catch {
        toast.error("Failed to load invites.");
      }
    },
    [page],
  );

  useEffect(() => {
    void load();
  }, [load]);

  async function handleSend(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    try {
      await sendInvite(email.trim());
      toast.success(`Invite sent to ${email}`);
      setEmail("");
      await load(1);
      setPage(1);
    } catch {
      toast.error("Failed to send invite.");
    } finally {
      setSending(false);
    }
  }

  async function handleResend(id: string) {
    setActionLoading(id);
    try {
      await resendInvite(id);
      toast.success("Invite resent.");
      await load();
    } catch {
      toast.error("Failed to resend invite.");
    } finally {
      setActionLoading(null);
    }
  }

  async function handleDelete(id: string) {
    setActionLoading(id);
    try {
      await deleteInvite(id);
      toast.success("Invitation deleted.");
      setInvites((prev) => prev?.filter((inv) => inv.id !== id) ?? null);
      setTotal((t) => t - 1);
    } catch (err) {
      console.error("[handleDelete]", err);
      toast.error("Failed to delete invitation.");
    } finally {
      setActionLoading(null);
    }
  }

  const totalPages = Math.ceil(total / 50);

  return (
    <div className="space-y-6">
      <form
        onSubmit={(e) => void handleSend(e)}
        className="flex flex-col gap-3 sm:flex-row sm:items-end"
      >
        <div className="space-y-1.5 flex-1">
          <Label htmlFor="invite-email">Send invite</Label>
          <Input
            id="invite-email"
            type="email"
            placeholder="email@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="sm:max-w-xs"
          />
        </div>
        <Button type="submit" disabled={sending} className="min-h-[44px]">
          {sending ? "Sending…" : "Send invite"}
        </Button>
      </form>

      <Separator />

      {invites === null ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-14 rounded-lg" />
          ))}
        </div>
      ) : invites.length === 0 ? (
        <p className="py-8 text-center text-muted-foreground">
          No invitations sent yet.
        </p>
      ) : (
        <div className="space-y-2">
          {invites.map((inv) => (
            <InviteRow
              key={inv.id}
              inv={inv}
              loading={actionLoading === inv.id}
              onResend={(id) => void handleResend(id)}
              onDelete={(id) => void handleDelete(id)}
            />
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Audit Logs tab ─────────────────────────────────────────────────────────

function AuditLogsTab() {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filterEvent, setFilterEvent] = useState("");

  async function load(cursor?: string) {
    cursor ? setLoadingMore(true) : setLoading(true);
    try {
      const res = await getAdminAuditLogs({
        ...(cursor ? { cursor } : {}),
        ...(filterEvent ? { eventType: filterEvent } : {}),
      });
      setLogs((prev) => (cursor ? [...prev, ...res.data] : res.data));
      setNextCursor(res.nextCursor);
    } catch {
      toast.error("Failed to load audit logs.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }

  useEffect(() => {
    setLogs([]);
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterEvent]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Input
          placeholder="Filter by event type…"
          value={filterEvent}
          onChange={(e) => setFilterEvent(e.target.value)}
          className="max-w-xs"
        />
      </div>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-12 rounded-lg" />
          ))}
        </div>
      ) : logs.length === 0 ? (
        <p className="py-8 text-center text-muted-foreground">
          No events recorded yet.
        </p>
      ) : (
        <>
          <div className="space-y-1.5">
            {logs.map((log) => (
              <Card key={log.id} className="p-3">
                <div className="flex flex-wrap gap-2 items-start justify-between">
                  <div>
                    <span className="text-sm font-mono">{log.eventType}</span>
                    {log.outcome && (
                      <Badge
                        variant={
                          log.outcome === "success" ? "default" : "destructive"
                        }
                        className="ml-2 text-xs"
                      >
                        {log.outcome}
                      </Badge>
                    )}
                    {log.provider && (
                      <Badge
                        variant="outline"
                        className="ml-1 text-xs capitalize"
                      >
                        {log.provider}
                      </Badge>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">
                    {formatDate(log.createdAt)}
                  </span>
                </div>
                {log.userId && (
                  <div className="text-xs text-muted-foreground mt-0.5 font-mono truncate">
                    user: {log.userId}
                  </div>
                )}
              </Card>
            ))}
          </div>

          {nextCursor && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                disabled={loadingMore}
                onClick={() => void load(nextCursor)}
              >
                {loadingMore ? "Loading…" : "Load more"}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Admin page ─────────────────────────────────────────────────────────────

export function AdminPage() {
  const { auth } = useAuth();

  if (auth.status === "loading") {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (auth.status !== "active" || auth.user.role !== "admin") {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold tracking-tight">Admin</h1>

      <Tabs defaultValue="users">
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="invitations">Invitations</TabsTrigger>
          <TabsTrigger value="logs">Audit Logs</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-4">
          <UsersTab />
        </TabsContent>
        <TabsContent value="invitations" className="mt-4">
          <InvitationsTab />
        </TabsContent>
        <TabsContent value="logs" className="mt-4">
          <AuditLogsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
